import { ref, computed, watch } from 'vue'
import Swal from 'sweetalert2/dist/sweetalert2.js'
import { debounce } from 'lodash'
import { showAddressRequiredModal, hasDeliveryData, isPickup } from './useAddressModal'
import { useDeliveryConfig } from './useDeliveryConfig'
import { trackAddToCart, trackRemoveFromCart } from './useGTMTracking'
import { trackMetaAddToCart, trackMetaRemoveFromCart } from './useMetaTracking'

const cartItems = ref([])
const selectedDeliveryOption = ref(null)
const isUpdatingCart = ref(false)
const appliedDiscount = ref(null)

// Get delivery config functionality
const { deliveryConfig, getDeliveryConfig } = useDeliveryConfig()

const count = computed(() =>
    cartItems.value.reduce((sum, item) => sum + item.quantity, 0)
)

const deliveryCost = computed(() => {
    if (!selectedDeliveryOption.value || selectedDeliveryOption.value.id === 'pickup') {
        return 0
    }
    return parseFloat(selectedDeliveryOption.value.price || 0)
})

const itemsTotal = computed(() =>
    cartItems.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
)

const discountAmount = computed(() => {
    if (!appliedDiscount.value) return 0;

    const discount = appliedDiscount.value;
    let amount = 0;

    if (discount.type === 'percentage') {
        amount = (itemsTotal.value * discount.value) / 100;
    } else if (discount.type === 'fixed') {
        amount = Math.min(discount.value, itemsTotal.value); // Don't exceed subtotal
    } else if (discount.type === 'free_shipping') {
        // Only apply free shipping discount if delivery is selected (not pickup)
        amount = (selectedDeliveryOption.value && selectedDeliveryOption.value.id !== 'pickup')
            ? deliveryCost.value
            : 0;
    }

    if (discount.includes_free_shipping && discount.type !== 'free_shipping') {
        if (selectedDeliveryOption.value && selectedDeliveryOption.value.id !== 'pickup') {
            amount += deliveryCost.value;
        }
    }

    return amount;
});

const total = computed(() => itemsTotal.value + deliveryCost.value - discountAmount.value)
const item_total = computed(() => itemsTotal.value)

const freeShippingProgress = computed(() => {
    const cfg = deliveryConfig.value
    if (!cfg || !cfg.freeShippingEnabled || !cfg.freeShippingThreshold || cfg.freeShippingThreshold <= 0) {
        return { enabled: false, threshold: 0, compareBase: 0, met: false, amountNeeded: 0 }
    }

    const basis = cfg.freeShippingBasis === 'post_discount' ? 'post_discount' : 'pre_discount'
    const compareBase = basis === 'post_discount'
        ? Math.max(0, itemsTotal.value - discountAmount.value)
        : itemsTotal.value
    const met = compareBase >= cfg.freeShippingThreshold

    return {
        enabled: true,
        threshold: cfg.freeShippingThreshold,
        basis,
        compareBase,
        met,
        amountNeeded: met ? 0 : cfg.freeShippingThreshold - compareBase,
    }
})

const gst = computed(() => total.value - total.value / 1.1)
const subtotal = computed(() => total.value - gst.value)

const openSideCart = debounce(() => {
    document.querySelector('.cart-link').dispatchEvent(new Event('click'))
    // Dispatch custom event for tracking
    window.dispatchEvent(new CustomEvent('cart:opened'))
}, 500)

function doAdd(product) {
    const existing = cartItems.value.find(
        (item) => item.id === product.id && item.type === product.type
    )
    if (existing) {
        let newQuantity = existing.quantity + product.quantity

        // Enforce max quantity limit for batches
        if (product.max_quantity && newQuantity > product.max_quantity) {
            newQuantity = product.max_quantity
        }

        const addedQuantity = newQuantity - existing.quantity
        existing.quantity = newQuantity

        // Track only the added quantity
        trackAddToCart(product, addedQuantity)
        trackMetaAddToCart(product, addedQuantity)
    } else {
        let quantity = product.quantity

        // Enforce max quantity limit for batches
        if (product.max_quantity && quantity > product.max_quantity) {
            quantity = product.max_quantity
        }

        cartItems.value.push({ ...product, quantity })

        // Track the added quantity
        trackAddToCart(product, quantity)
        trackMetaAddToCart(product, quantity)
    }
}

function addToCart(products) {
    // Check if delivery data exists
    // hasDeliveryData() will now return true if delivery is disabled and pickup is enabled
    if (!hasDeliveryData()) {
        // If no delivery data, show the modal
        showAddressRequiredModal(products, addToCart);
        return;
    }

    // If delivery data exists, proceed with adding to cart
    if (!products.id) {
        products.forEach((i) => doAdd(i))
    } else {
        doAdd(products)
    }
    openSideCart()
    saveCart()
}

async function promptRemoval(itemLabel, onConfirm, onCancel) {

    const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
            confirmButton: "btn btn-primary btn-rounded g-1",
            cancelButton: "btn btn-dark btn-rounded "
        },
        buttonsStyling: false
    });

    const { isConfirmed } = await swalWithBootstrapButtons.fire({
        title: `Remove "${itemLabel}"?`,
        text: 'This will remove the product from your cart.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it',
        cancelButtonText: 'No, keep it',
    })
    if (isConfirmed) {
        onConfirm()
    } else if (onCancel) {
        onCancel()
    }
}

function cartItemLabel(item) {
    return [item.name, item.variant]
        .filter((part) => part !== undefined && part !== null && String(part).trim() !== '')
        .join(' ')
}

async function updateQuantity(product) {

    // Check if delivery data exists
    if (!hasDeliveryData() && !isPickup()) {
        // If no delivery data, show the modal
        showAddressRequiredModal(product, updateQuantity);
        return;
    }

    const existing = cartItems.value.find(
        (item) => item.id === product.id && item.type === product.type
    )

    if (existing) {
        if (product.quantity > 0) {
            let newQuantity = product.quantity

            // Enforce max quantity limit for batches
            if (product.max_quantity && newQuantity > product.max_quantity) {
                newQuantity = product.max_quantity
            }

            openSideCart();

            // Track quantity change (only if increasing)
            const quantityDifference = newQuantity - existing.quantity
            if (quantityDifference > 0) {
                trackAddToCart(product, quantityDifference)
                trackMetaAddToCart(product, quantityDifference)
            }

            existing.quantity = newQuantity
        } else {
            // ask before removal
            await promptRemoval(
                cartItemLabel(product),
                () => {
                    // Track removal before actually removing
                    trackRemoveFromCart(existing)
                    trackMetaRemoveFromCart(existing)
                    const idx = cartItems.value.indexOf(existing)
                    if (idx > -1) cartItems.value.splice(idx, 1)
                },
                () => {
                    openSideCart();
                    existing.quantity = 1;
                }
            )
            return saveCart()
        }
    } else if (product.quantity > 0) {
        let quantity = product.quantity

        // Enforce max quantity limit for batches
        if (product.max_quantity && quantity > product.max_quantity) {
            quantity = product.max_quantity
        }

        openSideCart();
        cartItems.value.push({ ...product, quantity })

        // Track the added quantity
        trackAddToCart(product, quantity)
        trackMetaAddToCart(product, quantity)
    }

    saveCart()
}

async function removeFromCart(index) {
    const item = cartItems.value[index]
    if (!item) return
    await promptRemoval(
        cartItemLabel(item),
        () => {
            // Track removal before actually removing
            trackRemoveFromCart(item)
            trackMetaRemoveFromCart(item)
            cartItems.value.splice(index, 1)
            saveCart()
        }
    )
}

async function increaseQuantity(obj) {
    if (obj.max_quantity && obj.quantity >= obj.max_quantity) {
        return // Can't increase beyond max quantity
    }
    obj.quantity++
    saveCart()
}

async function decreaseQuantity(obj) {
    if (obj.quantity > 1) {
        obj.quantity--
        return saveCart()
    }
    // quantity === 1, prompt removal
    await promptRemoval(
        cartItemLabel(obj),
        () => {
            // Track removal before actually removing
            trackRemoveFromCart(obj)
            const idx = cartItems.value.indexOf(obj)
            if (idx > -1) cartItems.value.splice(idx, 1)
            saveCart()
        },
        () => {
            obj.quantity = 1
        }
    )
}

function formatPrice(val) {
    return formatCurrency(val)
}

function formatCurrency(amount) {
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
    }).format(amount)
}

const CART_KEY = 'ec_retail'

const saveCart = debounce(async (callback, userData = null) => {

    localStorage.setItem(CART_KEY, JSON.stringify({
        items: cartItems.value,
        deliveryOption: selectedDeliveryOption.value,
        appliedDiscount: appliedDiscount.value
    }))

    isUpdatingCart.value = true
    try {
        const requestData = {
            cart: cartItems.value.map((item) => ({
                id: item.id,
                type: item.type,
                price: item.price,
                quantity: item.quantity,
                code: item.code,
            })),
            deliveryOption: selectedDeliveryOption.value,
            appliedDiscount: appliedDiscount.value
        };

        // Include user data if provided
        if (userData) {
            requestData.userData = userData;
        }

        const res = await fetch('/api/cart/update', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document
                    .querySelector('meta[name="_token"]')
                    .getAttribute('content'),
            },
            body: JSON.stringify(requestData),
        })

        const data = await res.json()
        if (data.csrf) {
            document
                .querySelector('meta[name="_token"]')
                .setAttribute('content', data.csrf)
        }

        // Server successfully saved cart - no need to update local state
        // Vue components handle all quantity limits, server just persists the data

        if (typeof callback === 'function') {
            callback(data)
        }
    } catch (error) {
        console.error('Failed to save cart:', error)
    } finally {
        isUpdatingCart.value = false
    }
}, 500)

function loadCart() {
    try {
        const saved = localStorage.getItem(CART_KEY)
        if (saved) {
            const parsedData = JSON.parse(saved)
            if (parsedData.items) {
                cartItems.value = parsedData.items
                selectedDeliveryOption.value = parsedData.deliveryOption
                appliedDiscount.value = parsedData.appliedDiscount || null
            } else {
                // Handle legacy format (array of items)
                cartItems.value = parsedData
            }
        }
    } catch (error) {
        console.error('Failed to load cart:', error)
    }
}

function resetCart() {
    localStorage.setItem(CART_KEY, '[]')
    cartItems.value = []
    appliedDiscount.value = null
}

function applyDiscount(discount) {
    appliedDiscount.value = discount;
    saveCart();
}

function removeDiscount() {
    appliedDiscount.value = null;
    saveCart();
}

function formatDiscountAmount(discount) {
    if (discount.type === 'percentage') {
        return `${discount.value}%`;
    } else if (discount.type === 'fixed') {
        return formatPrice(discount.value);
    } else if (discount.type === 'free_shipping') {
        return 'Free Shipping';
    }
    return '';
}

window.addEventListener('storage', (e) => {
    if (e.key === CART_KEY) {
        loadCart()
    }
})

function setDeliveryOption(option) {
    selectedDeliveryOption.value = option
    saveCart()
}

export function useCart() {
    return {
        cartItems,
        count,
        subtotal,
        gst,
        item_total,
        total,
        itemsTotal,
        deliveryCost,
        selectedDeliveryOption,
        deliveryConfig,
        appliedDiscount,
        discountAmount,
        setDeliveryOption,
        resetCart,
        decreaseQuantity,
        increaseQuantity,
        formatPrice,
        addToCart,
        loadCart,
        removeFromCart,
        saveCart,
        updateQuantity,
        isUpdatingCart,
        getDeliveryConfig,
        applyDiscount,
        removeDiscount,
        formatDiscountAmount,
        freeShippingProgress,
    }
}
